export interface LabelData {
  id: string;
  // The product this variant belongs to — label templates are assigned per
  // product (not per variant), so this is what "auto" template resolution
  // and "set as this product's default" key off.
  productId: string;
  name: string;
  sku: string;
  // The variant's most recently minted batch tracking code, or null if none
  // has been minted yet (see saleor/labels/batch_codes.py on the backend).
  batchNumber: string | null;
  price: { amount: number; currency: string } | null;
  imageUrl: string | null;
  // Product/variant images in display order. `imageUrl` remains the first
  // image for compatibility with older callers and saved layouts.
  imageUrls?: string[];
  // Public storefront page URL, if a storefront base is configured — encoded
  // into the QR code field, falling back to the SKU when empty.
  storefrontUrl: string | null;
}

export type PaperKind = "sheet" | "roll";

export interface PaperPreset {
  id: string;
  labelWidthMm: number;
  labelHeightMm: number;
  kind: PaperKind;
  // Sheet only: physical page size and its display name (A4/Letter). Columns/rows
  // are computed from these against the label size — see computeSheetGrid.
  pageWidthMm?: number;
  pageHeightMm?: number;
  sheetName?: string;
}

// A print margin around the sheet edge and a gap between labels — both
// typical for adhesive label sheets (e.g. Avery) and generous enough that
// most printers' unprintable edge won't clip a label.
export const SHEET_MARGIN_MM = 10;
export const LABEL_GAP_MM = 3;

export interface SheetGrid {
  columns: number;
  rows: number;
  perPage: number;
}

export const computeSheetGrid = (
  pageWidthMm: number,
  pageHeightMm: number,
  labelWidthMm: number,
  labelHeightMm: number,
): SheetGrid => {
  const columns = Math.max(
    1,
    Math.floor((pageWidthMm - 2 * SHEET_MARGIN_MM + LABEL_GAP_MM) / (labelWidthMm + LABEL_GAP_MM)),
  );
  const rows = Math.max(
    1,
    Math.floor(
      (pageHeightMm - 2 * SHEET_MARGIN_MM + LABEL_GAP_MM) / (labelHeightMm + LABEL_GAP_MM),
    ),
  );

  return { columns, rows, perPage: columns * rows };
};
